import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Chip,
  Alert,
} from '@mui/material';

import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';

import {
  useLazyGetDataIdsQuery,
} from '../../services/solutionAPI.ts';

// ===== APIレスポンス（/data/ids）の想定 =====
type ApiHasBlock = {
  data_type?: string;
  seed_id?: null | string | string[];
  has_analysis_result?: boolean;
  timestamp?: number | string | null;
} | Record<string, never>;

// APIの1行
type ApiIdsItem = {
  session_id: string;
  user_mail_address: string;
  company_name: string;
  industry: string;
  company_size: string;
  has_seeds: ApiHasBlock;
  has_gap: ApiHasBlock;
  has_roi: ApiHasBlock;
  has_proposal: ApiHasBlock;
};

// 画面表示用の行（UIキーは既存のまま維持）
type Item = {
  session_id: string;
  user_mail_address: string;
  company_name: string;
  industry: string;
  company_size: string;

  // 〇/× 表示用（DataGrid 側で valueFormatter）
  has_needs_seeds: boolean; // ← seeds の有無（既存キー名を維持）
  has_gap: boolean;
  has_roi: boolean;
  has_draft: boolean;       // ← proposal の有無（既存キー名を維持）

  // 表示用：メモ作成日時 / 保存日時（ms）
  memo_timestamp_ms: number; // ← has_seeds.timestamp を ms 化
  saved_at_ms: number;       // ← 4ブロックの最新 timestamp を ms 化

  // 復元用ヒント
  seeds_seed_id: 'NULL';
  gap_seed_ids: string[];
  roi_seed_ids: string[];
  draft_seed_ids: string[];
};

/* ========================= ツールバー ========================= */
function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ gap: 1, p: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarQuickFilter quickFilterProps={{ debounceMs: 300 }} />
    </GridToolbarContainer>
  );
}

/* ========================= ユーティリティ ========================= */
// seed_id を配列へ正規化（'NULL' は除外）
function toArraySeedId(v: null | string | string[] | undefined) {
  if (!v) return [];
  const arr = Array.isArray(v) ? v : [v];
  return arr.map(String).filter(s => s && s !== 'NULL');
}

// UNIX秒（number|string|null|undefined）→ number|null（秒）
function toUnixSec(x: number | string | null | undefined): number | null {
  if (x == null || x === '' || x === 'NULL') return null;
  const n = typeof x === 'string' ? Number(x) : x;
  return Number.isFinite(n) && n! > 0 ? Number(n) : null;
}

// 最新の UNIX秒（秒）を選ぶ
function pickLatestUnixSec(...secs: Array<number | string | null | undefined>) {
  const ns = secs.map(toUnixSec).filter((v): v is number => v != null);
  return ns.length ? Math.max(...ns) : null;
}

// UNIX秒 → ミリ秒（表示用）
function secToMs(sec?: number | string | null) {
  const n = toUnixSec(sec);
  return n ? n * 1000 : 0;
}

/* ========================= 本体 ========================= */
export default function ListPage() {
  const history = useHistory();
  const [triggerGetDataIds] = useLazyGetDataIdsQuery();

  // 検索フォーム
  const [companyKeyword, setCompanyKeyword] = useState('');
  const [authorKeyword, setAuthorKeyword] = useState('');

  // 表示状態
  const [rows, setRows] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const columns = useMemo<GridColDef<Item>[]>(
    () => [
      { field: 'session_id', headerName: 'セッションID', flex: 0.9, minWidth: 110, sortable: true, filterable: true },
      { field: 'company_name', headerName: '企業名',      flex: 1.4, minWidth: 180, sortable: true, filterable: true },
      { field: 'industry',     headerName: '業種',        flex: 1.0, minWidth: 120, sortable: true, filterable: true },
      { field: 'company_size', headerName: '企業規模',    flex: 1.0, minWidth: 110, sortable: true, filterable: true },
      {
        field: 'has_needs_seeds', headerName: 'シーズ', flex: 0.6, minWidth: 70,
        type: 'boolean', sortable: true, filterable: true,
        valueFormatter: (p) => (p.value ? '○' : '×'),
      },
      {
        field: 'has_gap', headerName: 'GAP', flex: 0.6, minWidth: 70,
        type: 'boolean', sortable: true, filterable: true,
        valueFormatter: (p) => (p.value ? '○' : '×'),
      },
      {
        field: 'has_roi', headerName: 'ROI', flex: 0.6, minWidth: 70,
        type: 'boolean', sortable: true, filterable: true,
        valueFormatter: (p) => (p.value ? '○' : '×'),
      },
      {
        field: 'has_draft', headerName: '提案書ドラフト', flex: 0.9, minWidth: 120,
        type: 'boolean', sortable: true, filterable: true,
        valueFormatter: (p) => (p.value ? '○' : '×'),
      },
      {
        field: 'user_mail_address', headerName: '作成者',
        flex: 1.2, minWidth: 160, sortable: true, filterable: true,
      },
      {
        field: 'memo_timestamp_ms', headerName: 'メモ作成日時',
        flex: 1.1, minWidth: 160, sortable: true, filterable: true,
        valueFormatter: (p) => (p.value ? new Date(p.value as number).toLocaleString() : ''),
      },
      {
        field: 'saved_at_ms', headerName: '保存日時',
        flex: 1.1, minWidth: 160, sortable: true, filterable: true,
        valueFormatter: (p) => (p.value ? new Date(p.value as number).toLocaleString() : ''),
      },
      {
        field: 'actions', headerName: '操作',
        sortable: false, filterable: false, flex: 0.8, minWidth: 130,
        renderCell: (params) => {
          const r = params.row;
          return (
            <Box sx={{ width: '100%', display: 'grid', gap: 0.5 }}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => {
                  // 復元ヒントを location.state で渡す（既存ルートはそのまま）
                  history.push(`/proposals/${r.session_id}/seeds`, {
                    restore: {
                      session_id: r.session_id,
                      seeds_seed_id: 'NULL',
                      gap_seed_ids: r.gap_seed_ids,
                      roi_seed_ids: r.roi_seed_ids,
                      draft_seed_ids: r.draft_seed_ids,
                    },
                  });
                }}
                sx={{ lineHeight: 1, minHeight: 28, fontSize: '0.75rem', px: 1.2 }}
              >
                詳細
              </Button>
            </Box>
          );
        },
      },
    ],
    [history]
  );

  const onSearch = async () => {
    setLoading(true);
    setErrorMsg(null);

    // クエリ（空は送らない）
    const params: any = {};
    if (companyKeyword.trim()) params.company_name = companyKeyword.trim();
    if (authorKeyword.trim())  params.user_mail_address = authorKeyword.trim();

    try {
      const res = await triggerGetDataIds(params).unwrap();
      const apiItems = (Array.isArray(res) ? res : []) as ApiIdsItem[];

      const mapped: Item[] = apiItems.map((x) => {
        // メモ作成日時 = has_seeds.timestamp
        const memoSec = x?.has_seeds?.timestamp ?? null;

        // 保存日時 = 4ブロック（seeds/gap/roi/proposal）の最新 timestamp
        const latestSec = pickLatestUnixSec(
          x?.has_seeds?.timestamp,
          x?.has_gap?.timestamp,
          x?.has_roi?.timestamp,
          x?.has_proposal?.timestamp
        );

        return {
          session_id: x.session_id,
          user_mail_address: x.user_mail_address,
          company_name: x.company_name,
          industry: x.industry,
          company_size: x.company_size,

          // ○/× = has_analysis_result
          has_needs_seeds: !!(x as any)?.has_seeds?.has_analysis_result,
          has_gap:         !!x?.has_gap?.has_analysis_result,
          has_roi:         !!x?.has_roi?.has_analysis_result,
          has_draft:       !!(x as any)?.has_proposal?.has_analysis_result,

          // UNIX秒 → ms
          memo_timestamp_ms: secToMs(memoSec),
          saved_at_ms:      secToMs(latestSec),

          // seed_id 情報（'NULL' は除外して保持）
          seeds_seed_id: 'NULL',
          gap_seed_ids:   toArraySeedId(x?.has_gap?.seed_id),
          roi_seed_ids:   toArraySeedId(x?.has_roi?.seed_id),
          draft_seed_ids: toArraySeedId((x as any)?.has_proposal?.seed_id),
        };
      });

      setRows(mapped);
    } catch (e: any) {
      setRows([]);
      setErrorMsg('一覧取得に失敗しました（/data/ids）。権限/トークン/パラメータをご確認ください。');
    } finally {
      setLoading(false);
    }
  };

  const onCreateNew = () => {
    history.push('/proposals/new/seeds');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        ソリューション提案一覧
      </Typography>

      <Grid container spacing={1} alignItems="flex-end" sx={{ maxWidth: 900, mb: 1 }}>
        <Grid item xs={12} sm={5.5}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            企業名
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="検索ワード"
            value={companyKeyword}
            onChange={(e) => setCompanyKeyword(e.target.value)}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm="auto"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pb: { xs: 0.5, sm: 0 } }}
        >
          <Chip label="AND" size="small" variant="outlined" />
        </Grid>

        <Grid item xs={12} sm={5.5}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            作成者
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="メールアドレス"
            value={authorKeyword}
            onChange={(e) => setAuthorKeyword(e.target.value)}
          />
        </Grid>

        <Grid item xs="auto">
          <Button variant="contained" color="primary" onClick={onSearch} sx={{ minWidth: 96 }} disabled={loading}>
            検索
          </Button>
        </Grid>

        <Grid item xs="auto">
          <Button variant="contained" color="primary" onClick={onCreateNew} sx={{ minWidth: 96 }}>
            新規追加
          </Button>
        </Grid>
      </Grid>

      {errorMsg && <Alert severity="error" sx={{ mb: 1 }}>{errorMsg}</Alert>}

      <Box sx={{ height: 520, width: '100%' }}>
        <DataGrid
          loading={loading}
          rows={rows.map((r) => ({ id: r.session_id, ...r }))}
          columns={columns}
          getRowHeight={() => 'auto'}
          density="compact"
          sx={{
            '& .MuiDataGrid-columnHeaders': { fontSize: '0.8125rem' },
            '& .MuiDataGrid-cell': { fontSize: '0.8125rem', py: 0.5 },
            '& .MuiDataGrid-cellContent, & .MuiDataGrid-columnHeaderTitle': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          slots={{ toolbar: CustomToolbar }}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
          aria-label="solution-proposals-grid"
        />
      </Box>
    </Box>
  );
}